import React, { useEffect, useState } from 'react';

import { ProfileData, supabase, useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import { useResponsiveScreenSizes } from 'hooks/useResponsiveScreenSizes';

import { ProfileCard } from 'components/ui/ProfileCard';
import { Loader } from 'components/ui/Loader';

import { colors, Fonts, spacing, transparencies } from 'styles/theme';
import {
    Bumper,
    Card,
    ContentScroller,
    Divider,
    GridListWrapper,
} from 'styles/shared';
import { PursuitProfileRawData } from './ProfileContainer';

export const Directory = () => {
    const { userId, userProfile } = useAuth();
    const { showErrorToast } = useToast();
    const { isMobileScreenSize } = useResponsiveScreenSizes();

    const [profiles, setProfiles] = useState<Array<ProfileData>>([]);

    useEffect(() => {
        const fetchProfiles = async () => {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .order('full_name');

            if (error != null) {
                showErrorToast(error.message);
            } else {
                setProfiles(
                    data
                        .filter(
                            (profile) =>
                                profile.type === 'Pursuit Retreat' ||
                                profile.type === 'Pursuit Digital' ||
                                profile.type === 'Pursuit Coach'
                        )
                        .sort((a, b) => {
                            if (a.id === userId) {
                                return -1;
                            }
                            if (b.id === userId) {
                                return 1;
                            }
                            const aRawdata =
                                a.raw_data as PursuitProfileRawData | null;
                            const bRawData =
                                b.raw_data as PursuitProfileRawData | null;
                            if (
                                aRawdata?.is_published &&
                                !bRawData?.is_published
                            ) {
                                return -1;
                            }
                            if (
                                !aRawdata?.is_published &&
                                bRawData?.is_published
                            ) {
                                return 1;
                            }
                            return 0;
                        })
                );
            }
        };

        if (userId !== '') {
            fetchProfiles();
        }
    }, [showErrorToast, userId]);

    return (
        <ContentScroller>
            <Bumper $minHeight={spacing.xxxLarge} $shouldHideOnMobile />
            <Bumper $minHeight={spacing.xxxLarge} $shouldHideOnMobile />
            <Bumper $minHeight={spacing.xxSmall} $shouldHideOnDesktop />
            <Card $backgroundColor={transparencies.white95}>
                {isMobileScreenSize ? (
                    <Fonts.Heading6>{`${
                        userProfile?.type === 'Pursuit Coach'
                            ? 'Pursuit'
                            : 'Member'
                    } Directory`}</Fonts.Heading6>
                ) : (
                    <Fonts.Heading5>{`${
                        userProfile?.type === 'Pursuit Coach'
                            ? 'Pursuit'
                            : 'Member'
                    } Directory`}</Fonts.Heading5>
                )}
                {profiles.length === 0 ? (
                    <Loader $color={colors.grandGreen} />
                ) : (
                    <>
                        <Divider />
                        <Bumper $minHeight={spacing.xxSmall} />
                        <GridListWrapper $gap={spacing.large}>
                            {profiles.map((profile) => {
                                const parsedRawData = JSON.parse(
                                    JSON.stringify(profile.raw_data ?? {})
                                );

                                return (
                                    <ProfileCard
                                        key={profile.id}
                                        profileId={profile.id}
                                        fullName={profile.full_name ?? ''}
                                        imageUrl={
                                            parsedRawData?.profile_image_url ??
                                            ''
                                        }
                                        headline={parsedRawData?.headline ?? ''}
                                        isCoach={profile.is_coach ?? false}
                                        isHq={profile.is_hq ?? false}
                                        isPublished={
                                            parsedRawData?.is_published ?? false
                                        }
                                    />
                                );
                            })}
                        </GridListWrapper>
                    </>
                )}
            </Card>
            <Bumper $shouldHideOnMobile />
            <Bumper $shouldHideOnMobile />
            <Bumper />
        </ContentScroller>
    );
};
